import { useEffect, useRef, type KeyboardEvent } from 'react'
import { activity } from '../../../app/activity'
import { closePlane, showPlane } from '../places/modal'

/** The native top layer owns stacking; this fixed detail plane keeps focus and scroll. */
export function useMessageDialog(placeId: string) {
  const dialog = useRef<HTMLDialogElement>(null)
  const trigger = useRef<HTMLElement | null>(null)
  const opened = useRef(false)
  const close = () => {
    if (!opened.current) return
    opened.current = false
    if (dialog.current?.open) closePlane(dialog.current)
    activity.setSheetOpen(false)
    trigger.current?.focus({ preventScroll: true })
  }
  const show = (target: HTMLElement) => {
    if (!dialog.current || dialog.current.open) return
    trigger.current = target
    showPlane(dialog.current)
    dialog.current.querySelector<HTMLElement>('button')?.focus({ preventScroll: true })
    opened.current = true
    activity.setSheetOpen(true)
  }
  const onKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    event.stopPropagation()
    if (event.key === 'Escape' && event.currentTarget.hasAttribute('popover')) { event.preventDefault(); close(); return }
    if (event.key !== 'Tab') return
    const targets = event.currentTarget.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')
    const first = targets[0], last = targets[targets.length - 1]
    if (!first) { event.preventDefault(); return }
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
  }
  useEffect(() => {
    const focus = (event: FocusEvent) => {
      if (opened.current && event.target instanceof Node && !dialog.current?.contains(event.target)) dialog.current?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true })
    }
    document.addEventListener('focusin', focus)
    return () => { document.removeEventListener('focusin', focus); if (opened.current) close() }
  }, [placeId])
  return { dialog, show, close, onKeyDown }
}
